import React, { FC, useState } from 'react';
import cx from 'classnames';

import useThemeBlock from '@/hooks/useThemeBlock';
import { Image } from '@/components/molecules/Image/render';
import { Modal } from '@/components/molecules/Modal/render';

import block from './block.json';
import { getStyles, getModalStyles } from './styles.css';

/**
 * TYPINGS
 */
type CardResourceProps = ThemeProps & {
	type?: 'cardArticle' | 'cardVideo' | 'cardDocument';
	uptitle?: string;
	title: string;
	cta: string;
	description?: string;
	image?: ImageProps;
	link?: string;
	file?: any;
	videoType?: 'youtube' | 'vimeo' | 'other';
	videoId?: string;
};
/**
 * COMPONENTS
 */
export const CardResource: FC<CardResourceProps> & BlockConfigs = ({
	type = 'article',
	uptitle,
	title,
	cta,
	description,
	image,
	link,
	file,
	videoType,
	videoId,
	theme = {},
}) => {
	const { variant, blockTheme } = useThemeBlock(block.slug, theme);

	const url = file?.url || link || undefined;
	const [modalIsOpen, setModalIsOpen] = useState(false);

	const rootClass = cx(
		'supt-card supt-cardResource',
		`supt-cardResource--${type}`,
		getStyles(blockTheme),
		(url || videoId) && 'supt-withClick'
	);

	const playIcon = (
		<svg
			className="supt-cardResource__imagePlay"
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 48 48"
		>
			<g fill="none" fillRule="evenodd">
				<circle fill="#0271D2" cx="24" cy="24" r="24" />
				<path fill="#FFF" d="M17 14v20l19-9.913z" />
			</g>
		</svg>
	);

	const handleOpenModal = () => {
		setModalIsOpen(true);
	};

	const handleCloseModal = () => {
		setModalIsOpen(false);
	};

	const getVideoUrl = () => {
		if (videoType === 'youtube') {
			return '//www.youtube.com/embed/' + videoId;
		} else if (videoType === 'vimeo') {
			return '//player.vimeo.com/video/' + videoId;
		}
	};

	return (
		<div
			className={rootClass}
			data-block={block.slug}
			data-variant={variant}
		>
			{uptitle ? (
				<div className="supt-cardResource__uptitleWrapper">
					<p
						className="supt-cardResource__uptitle"
						dangerouslySetInnerHTML={{ __html: uptitle }}
					/>
				</div>
			) : null}
			{type === 'video' && image ? (
				<div className="supt-cardResource__imageWrapper">
					{playIcon}
					<Image {...image} className="supt-cardResource__image" />
				</div>
			) : null}
			<div className="supt-cardResource__inner">
				<div className="supt-cardResource__content">
					<h4
						className="supt-cardResource__title"
						dangerouslySetInnerHTML={{ __html: title }}
					/>
					{description ? (
						<p
							className="supt-cardResource__description"
							dangerouslySetInnerHTML={{ __html: description }}
						/>
					) : null}
				</div>
				<div className="supt-cardResource__ctaWrapper">
					{url &&
					(type === 'article' ||
						type === 'document' ||
						(type === 'video' && videoType === 'other')) ? (
						<a
							href={url}
							className={cx(
								'supt-cardResource__cta',
								cta && 'supt-cardResource__cta--withArrow',
								'supt-cardResource__link'
							)}
							target="_blank"
							rel="noopener noreferrer"
							dangerouslySetInnerHTML={{ __html: cta }}
						/>
					) : type === 'video' && videoId ? (
						<button
							className={cx(
								'supt-cardResource__cta',
								cta && 'supt-cardResource__cta--withArrow',
								'supt-cardResource__link'
							)}
							dangerouslySetInnerHTML={{ __html: cta }}
							onClick={handleOpenModal}
						/>
					) : (
						<p
							className={cx(
								'supt-cardResource__cta',
								cta && 'supt-cardResource__cta--withArrow',
								'supt-cardResource__link'
							)}
							dangerouslySetInnerHTML={{ __html: cta }}
						/>
					)}
				</div>
			</div>

			{videoType === 'youtube' || videoType === 'vimeo' ? (
				<Modal
					isOpen={modalIsOpen}
					onClose={handleCloseModal}
					className={getModalStyles()}
				>
					<iframe
						width="460"
						height="230"
						src={getVideoUrl() + '?autoplay=1'}
						frameBorder="0"
						tabIndex={-1}
						allowFullScreen={true}
					/>
				</Modal>
			) : null}
		</div>
	);
};

CardResource.slug = block.slug;
CardResource.title = block.title;
